import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import { AideHouseholdData, emptyLiveInAideState } from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 3 — Household + HUD assistance (RESPROP LIA Questionnaire p1-2). Five
// Yes/No questions, all required. Reuses the affordable Y/N ToggleButton pattern
// (see HouseholdIncome): booleans stored as boolean|null, `null` == unanswered.
// The final question (evicted for lease violations in the past 5 years) reveals
// a required "please explain" textarea when Yes; when No the textarea is hidden
// AND cleared so stale text can't leak into the payload (mirrors the
// conditional-reveal pattern in AideCriminalScreening).
// ---------------------------------------------------------------------------

interface Props {
  /** Persisted household slice (state.household). */
  defaultValues?: Partial<AideHouseholdData>;
  onSubmit: (data: AideHouseholdData) => void | Promise<void>;
  isSaving?: boolean;
}

/** The five Y/N household questions, in questionnaire order. */
const QUESTION_KEYS: (keyof AideHouseholdData)[] = [
  "hudAssistance",
  "otherFamilyMoving",
  "pets",
  "goodStanding",
  "evicted",
];

export const AideHousehold: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const requiredMsg = t(
    "affordable.application.liveInAide.household.required"
  ) as string;

  const schema = yup.object({
    hudAssistance: yup.boolean().nullable().required(requiredMsg),
    otherFamilyMoving: yup.boolean().nullable().required(requiredMsg),
    pets: yup.boolean().nullable().required(requiredMsg),
    goodStanding: yup.boolean().nullable().required(requiredMsg),
    evicted: yup.boolean().nullable().required(requiredMsg),
    // Explain is required only when evicted in the past 5 years = Yes.
    evictedExplain: yup.string().when("evicted", {
      is: true,
      then: (s) =>
        s.required(
          t(
            "affordable.application.liveInAide.household.evictedExplainRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AideHouseholdData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      hudAssistance:
        defaultValues?.hudAssistance ??
        emptyLiveInAideState.household.hudAssistance,
      otherFamilyMoving:
        defaultValues?.otherFamilyMoving ??
        emptyLiveInAideState.household.otherFamilyMoving,
      pets: defaultValues?.pets ?? emptyLiveInAideState.household.pets,
      goodStanding:
        defaultValues?.goodStanding ??
        emptyLiveInAideState.household.goodStanding,
      evicted: defaultValues?.evicted ?? emptyLiveInAideState.household.evicted,
      evictedExplain:
        defaultValues?.evictedExplain ??
        emptyLiveInAideState.household.evictedExplain,
    },
  });

  const evicted = watch("evicted");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.household.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.household.subtitle")}
        </Typography>

        {QUESTION_KEYS.map((key) => (
          <React.Fragment key={key}>
            <Controller
              name={key}
              control={control}
              render={({ field }) => (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                      {t(
                        `affordable.application.liveInAide.household.questions.${key}`
                      )}
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{ color: "error.main", ml: 0.5 }}
                      >
                        *
                      </Box>
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={
                        field.value === true
                          ? "yes"
                          : field.value === false
                          ? "no"
                          : null
                      }
                      onChange={(_, val) => {
                        if (val === null) return;
                        const yes = val === "yes";
                        field.onChange(yes);
                        // When evicted switches to No, clear the revealed
                        // explanation so stale text can't leak into the payload.
                        if (key === "evicted" && !yes) {
                          setValue("evictedExplain", "");
                        }
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      <ToggleButton value="yes" sx={{ px: 2 }}>
                        {t("affordable.application.liveInAide.household.yes")}
                      </ToggleButton>
                      <ToggleButton value="no" sx={{ px: 2 }}>
                        {t("affordable.application.liveInAide.household.no")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {errors[key] && (
                    <FormHelperText error>
                      {errors[key]?.message as string}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />

            {/* Revealed only when evicted in the past 5 years = Yes. */}
            {key === "evicted" && evicted === true && (
              <Controller
                name="evictedExplain"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    label={`${t(
                      "affordable.application.liveInAide.household.evictedExplainLabel"
                    )} *`}
                    error={!!errors.evictedExplain}
                    helperText={errors.evictedExplain?.message}
                    sx={{ mb: 3 }}
                  />
                )}
              />
            )}
          </React.Fragment>
        ))}
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
